import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { refusalOfError } from "@/lib/api"
import { ConnectorProtocolError, readConnectorHealth, startGmailConsent, type ConnectorConnection } from "@/lib/connector-health"
import type { Scope } from "@/lib/connection"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useCell } from "@/lib/session"
import { RefusalNote } from "@/workspace/values"

const names = { erpnext: "ERPNext", gmail: "Gmail", posthog: "PostHog", cloudflare: "Cloudflare" } as const

export function ConnectorHealthPanel({ scope }: { scope: Scope }) {
  const { client, connection } = useCell()
  const t = useT()
  const health = useQuery({
    queryKey: ["connector-health", connection.baseUrl, scope.orgId, scope.environmentId],
    queryFn: ({ signal }) => readConnectorHealth(client, connection.baseUrl, scope, signal),
    retry: false,
  })
  if (health.isSuccess && health.data === null) return null
  return <section className="space-y-3" aria-label={t("connections.title")}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-[15px] font-semibold tracking-tight">{t("connections.title")}</h2>
      <Button size="sm" variant="outline" disabled={health.isFetching} onClick={() => void health.refetch()}>{t("connections.refresh")}</Button>
    </div>
    <p className="text-sm text-muted-foreground">{t("connections.description")}</p>
    {health.isError ? <HealthError error={health.error} /> : health.isPending ? <Skeleton className="h-24 w-full rounded-2xl" /> : health.data ? <>
      {health.data.connections.length === 0 ? <p className="text-sm text-muted-foreground">{t("connections.empty")}</p> : <ul className="divide-y divide-border rounded-2xl border border-border text-sm">
        {health.data.connections.map((item) => <ConnectionRow key={`${item.connectorId}/${item.tenantId}/${item.externalAccount}`} item={item} scope={scope} />)}
      </ul>}
    </> : null}
  </section>
}

function HealthError({ error }: { error: unknown }) {
  const t = useT()
  if (refusalOfError(error)) return <RefusalNote error={error} title={t("connections.unavailable")} />
  return <p role="alert" className="rounded-xl border border-destructive/30 px-4 py-3 text-sm text-destructive">{t(error instanceof ConnectorProtocolError ? "connections.protocolError" : "connections.unavailable")}</p>
}

function ConnectionRow({ item, scope }: { item: ConnectorConnection; scope: Scope }) {
  const t = useT()
  const formatNumber = useFormatNumber()
  const { client, connection } = useCell()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const callback = new URL("/v1/connectors/gmail/oauth/callback", connection.baseUrl).href
  const needsConsent = item.connectorId === "gmail" && item.status === "awaiting_consent" && !item.missingCredentials.some((key) => key !== "NORN_GMAIL_REFRESH_TOKEN")
  async function connect() {
    setPending(true)
    setError(null)
    try { window.location.assign(await startGmailConsent(client, connection.baseUrl, scope)) }
    catch (cause) {
      setError(cause)
      setPending(false)
      await queryClient.invalidateQueries({ queryKey: ["connector-health", connection.baseUrl, scope.orgId, scope.environmentId] })
    }
  }
  return <li className="space-y-3 px-4 py-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-medium">{names[item.connectorId]}</p>
      <span className={`rounded-full border px-2.5 py-1 text-2xs ${item.status === "error" ? "border-destructive/30 text-destructive" : item.status === "healthy" ? "border-success/40 text-success" : "border-border text-muted-foreground"}`}>{t(`connections.status.${item.status}`)}</span>
    </div>
    <p className="text-xs text-muted-foreground">{t("connections.readOnly")}</p>
    {item.missingCredentials.length > 0 ? <div className="space-y-1 text-xs"><p className="text-muted-foreground">{t("connections.missing")}</p><ul className="space-y-1">{item.missingCredentials.map((key) => <li key={key}><code dir="ltr" className="break-all font-mono">{key}</code></li>)}</ul></div> : null}
    <dl className="grid gap-3 text-xs sm:grid-cols-3">
      <div><dt className="text-muted-foreground">{t("connections.lastSuccess")}</dt><dd className="mt-1">{item.lastSuccessAt ? <time dateTime={item.lastSuccessAt} dir="ltr">{item.lastSuccessAt}</time> : t("connections.never")}</dd></div>
      <div><dt className="text-muted-foreground">{t("connections.records")}</dt><dd className="mt-1 tabular-nums">{formatNumber(item.records)}</dd></div>
      <div><dt className="text-muted-foreground">{t("connections.retry")}</dt><dd className="mt-1">{item.nextRetryAt ? <time dateTime={item.nextRetryAt} dir="ltr">{item.nextRetryAt}</time> : t("connections.noRetry")}</dd></div>
    </dl>
    {item.lastErrorCode ? <p className="text-xs text-destructive">{t("connections.failure")} <code dir="ltr" className="break-all font-mono">{item.lastErrorCode}</code></p> : null}
    {item.connectorId === "gmail" ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">{t("connections.gmailSetup")}</summary><p className="mt-2 text-muted-foreground">{t("connections.callback")}</p><code dir="ltr" className="mt-1 block break-all font-mono">{callback}</code></details> : null}
    {needsConsent ? <Button size="sm" variant="outline" disabled={pending} onClick={() => void connect()}>{t(pending ? "connections.connecting" : "connections.connectGmail")}</Button> : null}
    {error ? <HealthError error={error} /> : null}
  </li>
}
